import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Share2, Image as ImageIcon, Download,
} from "lucide-react";
import { PILL } from "./shared";

export default function ShareTab({ biz }) {
  const [generating, setGenerating] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [bust, setBust] = useState(Date.now());
  const [hasAi, setHasAi] = useState(false);
  const [format, setFormat] = useState("square");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const cardEndpoint = (fmt) => `${BACKEND_URL}/api/share-card/${fmt}.png`;
  const shareText = encodeURIComponent(`We just launched ${biz.summary?.name || "our new website"}! ✨`);
  const publicUrl = `${window.location.origin}/site/${biz.slug}`;

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;
    (async () => {
      setLoadingPreview(true);
      try {
        const res = await fetch(cardEndpoint(format), { credentials: "include" });
        if (!res.ok) throw new Error("preview failed");
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch {
        if (!cancelled) toast.error("Couldn't load preview");
      }
      if (!cancelled) setLoadingPreview(false);
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [format, bust]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/share-card/status");
        setHasAi(!!data.has_ai_bg);
      } catch { /* non-fatal */ }
    })();
  }, [bust]);

  const generateAi = async () => {
    setGenerating(true);
    try {
      await api.post("/share-card/generate-ai-bg");
      toast.success("AI background generated");
      setBust(Date.now());
    } catch {
      toast.error("Couldn't create AI art right now — using your brand gradient.");
    }
    setGenerating(false);
  };
  const clearAi = async () => {
    setClearing(true);
    try {
      await api.delete("/share-card/ai-bg");
      toast.success("Cleared");
      setBust(Date.now());
    } catch { toast.error("Failed"); }
    setClearing(false);
  };

  const download = async (fmt) => {
    try {
      const res = await fetch(cardEndpoint(fmt), { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${biz.slug}-${fmt}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  return (
    <div>
      <div className="nb-card p-5 mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="font-display text-2xl font-extrabold flex items-center gap-2"><Share2 className="w-6 h-6" /> Share your launch</div>
          <div className="text-sm text-gray-600 mt-1">Beautiful social cards with your headline and brand colors — ready for Instagram, LinkedIn, X.</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={generateAi} disabled={generating} className={`${PILL} bg-[#8B5CF6] text-white`} data-testid="gen-ai-bg-btn">
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
            {hasAi ? "Regenerate AI art" : "Add AI background"}
          </button>
          {hasAi && (
            <button onClick={clearAi} disabled={clearing} className={`${PILL} bg-white`} data-testid="clear-ai-bg-btn">
              {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Use gradient"}
            </button>
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4" data-testid="format-toggle">
        {[{ id: "square", label: "Instagram (1:1)" }, { id: "landscape", label: "LinkedIn / X (1200×630)" }, { id: "story", label: "Story (9:16)" }].map((f) => (
          <button key={f.id} onClick={() => setFormat(f.id)} data-testid={`fmt-${f.id}`}
            className={`${PILL} ${format === f.id ? "bg-[#111] text-white" : "bg-white"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="nb-card-flat p-4 bg-[#F5F0FF]">
          <div className="bg-white border-2 border-black rounded-xl overflow-hidden relative">
            {loadingPreview && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur z-10" data-testid="preview-loading">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
            {previewUrl ? (
              <img
                src={previewUrl}
                alt="Share card preview"
                data-testid="share-card-preview"
                className={`w-full block ${format === "story" ? "max-w-[360px] mx-auto" : ""}`}
              />
            ) : (
              <div className="w-full" style={{ aspectRatio: format === "square" ? "1/1" : format === "landscape" ? "1200/630" : "1080/1920" }} />
            )}
          </div>
        </div>

        <div className="space-y-3">
          <button onClick={() => download(format)} className={`${PILL} bg-[#10B981] text-white w-full justify-center`} data-testid="download-card-btn">
            <Download className="w-4 h-4" /> Download PNG
          </button>
          <a target="_blank" rel="noreferrer"
             href={`https://twitter.com/intent/tweet?text=${shareText}&url=${encodeURIComponent(publicUrl)}`}
             className={`${PILL} bg-white w-full justify-center`} data-testid="share-twitter-btn">
            Share on X
          </a>
          <a target="_blank" rel="noreferrer"
             href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(publicUrl)}`}
             className={`${PILL} bg-white w-full justify-center`} data-testid="share-linkedin-btn">
            Share on LinkedIn
          </a>
          <a target="_blank" rel="noreferrer"
             href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(publicUrl)}`}
             className={`${PILL} bg-white w-full justify-center`} data-testid="share-facebook-btn">
            Share on Facebook
          </a>
          <div className="nb-card-flat p-4 text-sm">
            <div className="font-bold mb-1">Pro tip</div>
            <p className="text-gray-700">Download the square version for Instagram, landscape for LinkedIn/X, and story for IG/FB stories. Tag friends so they share it too — word-of-mouth is your cheapest growth channel.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
